import { DateTime, Duration } from 'luxon';
import { UpdateUserRequest, User, UserDriverLic, UserDriverLicScanResponse, UserEquipment } from './user';
import { CycleBillingTerms } from '../order/order';
import Dinero from 'dinero.js';
import { Representative } from '../representatives/representative';
import { StateProvince } from '../locales/locales';

export namespace UserParsers {
  export class UserDriverLicenseJson {
    id!: number;
    userId!: number;
    fullName!: string;
    driverLicNum!: string;
    expirationDate!: string;
    state?: StateProvince;
    dateOfBirth?: string;
  }

  export function toUserDriverLicense(input: UserDriverLicenseJson): UserDriverLic {
    return {
      id: input.id,
      userId: input.userId,
      fullName: input.fullName,
      driverLicNum: input.driverLicNum,
      expirationDate: DateTime.fromISO(input.expirationDate, { setZone: true }),
      state: input.state,
      dateOfBirth: input.dateOfBirth ? DateTime.fromISO(input.dateOfBirth, { setZone: true }) : undefined,
    };
  }

  export class UserDriverLicenseScanJson {
    userId!: number;
    fullName!: string;
    driverLicNum!: string;
    expirationDate!: string;
    state!: StateProvince;
    dateOfBirth!: string;
  }

  export function toUserDriverLicenseScan(input: UserDriverLicenseScanJson): UserDriverLicScanResponse {
    return {
      userId: input.userId,
      fullName: input.fullName,
      driverLicNum: input.driverLicNum,
      expirationDate: DateTime.fromISO(input.expirationDate, { setZone: true }),
      state: input.state,
      dateOfBirth: input.dateOfBirth ? DateTime.fromISO(input.dateOfBirth, { setZone: true }) : undefined,
    };
  }

  export class UserEquipmentJson {
    id!: string;
    createdAt!: string;
    updatedAt!: string;
    userId!: number;
    description!: string;
    make?: string;
    model?: string;
    serialNumber?: string;
  }

  export function toUserEquipment(input: UserEquipmentJson): UserEquipment {
    return {
      id: input.id,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      updatedAt: DateTime.fromISO(input.updatedAt, { setZone: true }),
      userId: input.userId,
      description: input.description,
      make: input.make,
      model: input.model,
      serialNumber: input.serialNumber,
    };
  }

  export class CycleBillingTermsJson {
    cycleLength!: string;
    paymentPeriod!: string;
    cycleInvoiceDay!: number;
    isDayOfMonth!: boolean;
  }

  export function toCycleBillingTerms(input?: CycleBillingTermsJson): CycleBillingTerms | undefined {
    if (!input) return undefined;
    return {
      cycleLength: Duration.fromISO(input.cycleLength),
      paymentPeriod: Duration.fromISO(input.paymentPeriod),
      cycleInvoiceDay: input.cycleInvoiceDay,
      isDayOfMonth: input.isDayOfMonth,
    };
  }

  export type UserJson = Omit<User, 'cycleBillingTerms' | 'representatives' | 'creditLimit'> & {
    cycleBillingTerms?: CycleBillingTermsJson;
    creditLimit?: Dinero.Options;
  };

  export function toUser(input: UserJson): User {
    return {
      id: input.id,
      name: input.name,
      email: input.email,
      secondaryEmails: input.secondaryEmails,
      company: input.company,
      phone: input.phone,
      tagPricing: input.tagPricing,
      type: input.type,
      addedOn: input.addedOn,
      status: input.status,
      isDeleted: input.isDeleted,
      chargeTax: input.chargeTax,
      doNoRent: input.doNoRent,
      requirePoNum: input.requirePoNum,
      requireCompanyRep: input.requireCompanyRep,
      userReqDamageWaiver: input.userReqDamageWaiver,
      firstRental: input.firstRental,
      ordersPlaced: input.ordersPlaced,
      stripeCustomerId: input.stripeCustomerId,
      cycleBillingTerms: toCycleBillingTerms(input.cycleBillingTerms)!,
      structureType: input.structureType,
      creditLimit: input.creditLimit ? Dinero(input.creditLimit) : undefined,
      processingFeeOverride: input.processingFeeOverride,
      defaultSalesRepId: input.defaultSalesRepId,
      defaultTaxCode: input.defaultTaxCode,
      paymentBan: input.paymentBan,
    };
  }

  export function toUsers(input: UserJson[]): User[] {
    return input.map(toUser);
  }

  export function toUserRequest(user: User): UpdateUserRequest {
    return { ...user, creditLimit: user.creditLimit ? user.creditLimit.getAmount() : undefined };
  }

  export type RepresentativeJson = {
    id: string;
    name: string;
    phone?: string;
    title?: string;
    userId: number;
    email: string;
    createdAt: string;
    deletedAt?: string;
  };

  export function toRepresentative(input: RepresentativeJson): Representative {
    return {
      id: input.id,
      name: input.name,
      phone: input.phone,
      title: input.title,
      userId: input.userId,
      email: input.email,
      createdAt: DateTime.fromISO(input.createdAt),
      deletedAt: input.deletedAt ? DateTime.fromISO(input.createdAt) : undefined,
    };
  }
}
